// src/lib/facture-resa-html.js — Phase 2.E.11 (ADR-044)
//   + 06/05/2026 architecture multi-émetteur (Bug #1 audit visuel global).
// Générateur HTML imprimable d'une pièce voyageur par séjour (1 voyageur =
// 1 pièce). Style aligné avec la facture mensuelle proprio.
//
// ⚠️ SOURCE UNIQUE pour l'écran ET le serveur (décision 34, 30/10, lot A1) :
// écrit en CommonJS pur, comme src/lib/emitter-resolver.js ; le serveur le lit
// par le relais d'une ligne `lib/facture-resa-html.js`. Plus de miroir à tenir
// à l'octet près : il n'y a plus qu'une copie (règle 79).

const { resolveEmitter } = require('./emitter-resolver')

function genFactureResaHTML({ reservation, log, prop, cfg, mode = 'preview', invoice_number = null, prefix = null, previewNumber = null, issue_date = null }) {
  const r = reservation || {}
  const emitter = resolveEmitter({ cfg, prop })
  // Lot 3c (07/10/2026) : `issue_date` (opt-in) remplace la date du jour —
  // l'émission serveur y passe le jeton `__INVOICE_DATE__`, que la RPC
  // assign_invoice_number substitue par la date de PARIS dans la même
  // transaction que le numéro (le serveur Vercel est en UTC). Absent : rendu
  // byte-identique à avant.
  const dateEmission = issue_date || new Date().toLocaleDateString('fr-FR')
  // Phase 3 PR 6 (ADR-066) : si invoice_number fourni (issu du RPC séquentiel),
  // on l'utilise tel quel (numéro RÉEL consommé, JAMAIS de mention « (aperçu) »).
  //
  // Chantier C : sinon (aperçu avant émission), on affiche le PROCHAIN numéro
  // PRÉVU `{prefix}-{year}-{NNNN}` quand l'appelant a pu le calculer
  // (previewNumber). À défaut de previewNumber (série absente / séquences non
  // chargées), fallback historique `{prefix}-{UUID8}`. La prop `previewNumber`
  // est DISTINCTE d'`invoice_number` : elle ne doit JAMAIS atteindre
  // createInvoice ni servir de vrai numéro (lecture seule, rien n'est consommé
  // en DB).
  //
  // Correction pessimist : la mention « (aperçu) » est conditionnée à
  // `mode === 'preview'` (FactureTab). En `mode === 'email'` (ResaV2Modal envoie
  // un VRAI email voyageur SANS invoice_number) le numéro NE porte PAS « (aperçu) »
  // — comportement strictement identique à AVANT le chantier C (0-régression
  // chemin email). Quand `invoice_number` est fourni (facture émise), jamais de
  // mention, quel que soit le mode.
  const apercu = mode === 'preview' ? ' (aperçu)' : ''
  const numFact = invoice_number
    || (previewNumber
      ? `${previewNumber}${apercu}`
      : `${prefix || 'V'}-${(r.id || '').slice(0, 8).toUpperCase()}${apercu}`)

  // Bascule REÇU / FACTURE calculée DANS le générateur depuis l'émetteur résolu
  // (PAS un flag par appelant) : garantit aperçu ≡ émis ET protège la série V
  // FBZ (un émetteur 'fbz' reste byte-identique au comportement actuel).
  //   - emitter.type === 'proprio' → REÇU épuré au nom du proprio : nom seul en
  //     en-tête et émetteur, total « Total payé » en direct, « Total payé sur {plateforme} » détaillé hors direct,
  //     ligne taxe affichée pour TOUTE source si cityTax > 0 (libellé source-aware,
  //     Front 4 : direct/website « reversée à la commune » sinon « collectée par
  //     {Plateforme} »), footer vide, AUCUNE mention intermédiaire.
  //   - emitter.type === 'fbz' (sous-loc Maison Zen, série V) → FACTURE, rendu
  //     INCHANGÉ (byte-identique au comportement historique).
  const isReceipt = emitter.type === 'proprio'
  const docLabel = isReceipt ? 'REÇU' : 'Facture'

  const voyageurNom = [r.guest_first_name, r.guest_last_name].filter(Boolean).join(' ') || '—'
  const voyageurEmail = r.guest_email || ''
  const voyageurTel = r.guest_phone || ''
  const dateArrivee = r.start_date ? new Date(r.start_date).toLocaleDateString('fr-FR') : '—'
  const dateDepart = r.end_date ? new Date(r.end_date).toLocaleDateString('fr-FR') : '—'
  const nuits = (r.start_date && r.end_date) ? Math.max(1, Math.round((new Date(r.end_date) - new Date(r.start_date)) / 86400000)) : 1
  const nbVoyageurs = r.guest_count || 1
  const totalAmount = Number(r.total_amount) || 0
  const cleaningFee = Number(r.cleaning_fee) || 0
  const cityTax = Number(r.city_tax) || 0
  const baseHebergement = Math.max(0, totalAmount - cleaningFee - cityTax)

  // Taxe de séjour sur le REÇU proprio : ligne SÉPARÉE affichée pour TOUTE source
  // (Direct ET OTA) dès que cityTax > 0 — le reçu reflète le total réellement
  // déboursé par le voyageur (décision Dimitri).
  // Valeur = city_tax (Lot 6, déjà calculée) — aucun recalcul ici.
  // Front 4 (décision Dimitri) : le LIBELLÉ dépend de la source — direct/website
  // = « reversée à la commune » (FBZ reverse à la commune) ; OTA (airbnb/booking/
  // vrbo) = « collectée par {Plateforme} » (l'OTA collecte et reverse lui-même,
  // cf. #681). Changement de LIBELLÉ UNIQUEMENT — montant/total INCHANGÉS.
  const showReceiptTax = isReceipt && cityTax > 0
  // Total affiché sur le reçu en direct (« Total payé ») = lignes voyageur HORS commission,
  // taxe TOUJOURS incluse (découplé de totalAmount retourné, qui reste fiscal).
  const receiptTotalPaye = baseHebergement + cleaningFee + cityTax

  // Charte 2026 (maquette ref-recu-v4 validée) — habillage SEUL, calculs et
  // numérotation INCHANGÉS. Le reçu affiche l'émetteur en pied uniquement, au
  // format « Prénom Nom » UNIQUEMENT si les DEUX champs sont renseignés —
  // sinon repli sur le nom résolu (jamais un demi-nom « émis par Martin »).
  const emitterDisplay = isReceipt && prop?.prenom && prop?.nom_famille
    ? `${prop.prenom} ${prop.nom_famille}`
    : emitter.nom
  // Format d'AFFICHAGE des montants (virgule française, maquette) — les
  // calculs restent sur les nombres bruts, seul le rendu change.
  const fmtEuro = (n) => n.toFixed(2).replace('.', ',')

  // ── CONTACT DU PIED : LA CONFIG, JAMAIS UN LITTÉRAL (Dimitri 2026-08-03) ──
  // Le reçu et la facture sont des surfaces VOYAGEUR : « Une question ? … » ne
  // doit pas figer une adresse. MÊME RÈGLE que lib/contact-fbz-server.js
  // (`cfg.co.email`, chaîne non vide trimmée) — appliquée INLINE ici et NON
  // par un require : ce générateur a un MIROIR ESM byte-identique
  // (src/lib/facture-resa-html.js) qui ne peut pas charger un module CJS de
  // `lib/`. La règle est recopiée, la SOURCE reste unique (`cfg.co.email`).
  // FAIL-SOFT : rien de lisible ⇒ la ligne n'est PAS rendue (jamais une
  // adresse morte au bas d'un document fiscal archivé par le voyageur).
  const contactEmailDoc =
    cfg && cfg.co && typeof cfg.co.email === 'string' && cfg.co.email.trim()
      ? cfg.co.email.trim()
      : null
  // ── PLATEFORME : CE QUE L'ÉMETTEUR A FACTURÉ, RIEN D'AUTRE (décision 34, 30/10) ──
  // La pièce d'un séjour Airbnb ou Booking dit SEULEMENT ce que l'émetteur a
  // facturé, et précise que les frais de service et la taxe de séjour ont été
  // payés à la plateforme. Plus aucune saisie avant émission : le départ
  // automatique émet seul. Le montant vient d'UNE fonction,
  // montantFacturePlateforme (formules à valider par Dimitri sur relevé).
  // La commission de la plateforme n'apparaît jamais sur le document du
  // voyageur. Une plateforme sans règle (Vrbo, source vide ou inconnue) ne
  // s'émet pas (exception en mode 'email', avant tout numéro chez l'appelant).
  const viaPlateforme = r.source !== 'direct'
  const plateforme = viaPlateforme ? (PLATEFORMES[r.source] || null) : null
  const calcul = plateforme ? montantFacturePlateforme(r, emitter.type) : null
  if (mode === 'email' && viaPlateforme && !plateforme) throw new Error('plateforme_non_prise_en_charge')
  // Montant illisible (total absent ou nul, taxe Booking absente) : jamais un
  // document émis avec un montant deviné ; l'aperçu affiche « — ».
  if (mode === 'email' && calcul && calcul.erreur) throw new Error(calcul.erreur)
  const montantPlateforme = calcul && !calcul.erreur ? calcul : null
  const montantFacture = montantPlateforme ? montantPlateforme.montant : 0
  const taxeFactureeParEmetteur = !!(montantPlateforme && montantPlateforme.taxeIncluse > 0)
  const sejourFacture = montantPlateforme ? montantPlateforme.sejour : 0
  const nomPlateforme = plateforme ? plateforme.nom : 'la plateforme'
  const totalLabel = viaPlateforme ? 'Montant réglé' : 'Total payé'
  // Ménage connu : « N nuits × X € + ménage Y € ». Inconnu (cleaning_fee vide,
  // cas de toutes les réservations Beds24 au 08/10 : le prix transmis est le
  // TOTAL) : « N nuits », sans prix par nuit qui cacherait un ménage.
  const nuitsSejour = cleaningFee > 0
    ? `${nuits} nuit${nuits > 1 ? 's' : ''} × ${fmtEuro(Math.max(0, sejourFacture - cleaningFee) / nuits)} € + ménage ${fmtEuro(cleaningFee)} €`
    : `${nuits} nuit${nuits > 1 ? 's' : ''}`
  const notePlateforme = !plateforme
    ? 'Plateforme de réservation non prise en charge : ce document ne peut pas être émis.'
    : `Le paiement a été encaissé par ${plateforme.nom}. `
      + (taxeFactureeParEmetteur
        ? `Les frais de service ont été payés à ${plateforme.nom} ; la taxe de séjour, facturée par ${emitterDisplay}, est comprise dans ce montant. `
        : `Les frais de service et la taxe de séjour ont été payés à ${plateforme.nom}. `)
      + `Ce document atteste du séjour et du montant facturé par ${emitterDisplay}.`

  const emitterAddress = [emitter.adresse, [emitter.code_postal, emitter.ville].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ')
  // Pied du document : reçu = mention courte ; facture = mentions complètes
  // (raison sociale, capital, SIRET, TVA, adresse — maquette ref-facture-seule).
  const footerLine = isReceipt
    ? `Reçu n° ${escapeHtml(numFact)} · émis le ${escapeHtml(dateEmission)} par ${escapeHtml(emitterDisplay)}`
    : `Facture n° ${escapeHtml(numFact)} · émise le ${escapeHtml(dateEmission)} · ${escapeHtml(emitter.nom)}`
      + (emitter.isSousLoc ? ', SAS au capital de 100 €' : '')
      + (emitter.siret ? ` · SIRET ${escapeHtml(emitter.siret)}` : '')
      + (emitter.tva
        ? ` · TVA ${escapeHtml(emitter.tva)}`
        : (emitter.isSousLoc ? ' · TVA non applicable, art. 293 B du CGI' : ''))
      + (emitterAddress ? ` · ${escapeHtml(emitterAddress)}` : '')

  // Faille N1 (revue du 29/10, lot L5) : TOUTE valeur qui n'est pas un
  // littéral du gabarit ou un montant formaté passe par escapeHtml (nom du
  // voyageur dans <title> compris). Sans caractère spécial, escapeHtml rend
  // la chaîne telle quelle : le document d'une facture normale est inchangé
  // à l'octet près (test facture-resa-html.echappement.test.js).
  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8"/>
<title>${docLabel} ${escapeHtml(numFact)} — ${escapeHtml(voyageurNom)}</title>
<link href="https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;700;800&display=swap" rel="stylesheet">
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Raleway',-apple-system,'Segoe UI',Roboto,Arial,sans-serif;background:#eef1f4;padding:26px 12px;color:#1f2933}
  .page{background:#fff;max-width:760px;margin:0 auto;border-radius:12px;box-shadow:0 8px 30px rgba(31,41,51,.10);overflow:hidden}
  .head{padding:32px 48px 0 48px;text-align:center}
  .eyebrow{font-size:10px;letter-spacing:1.4px;color:#b4bcc4;text-transform:uppercase}
  .logement{font-size:17px;font-weight:700;color:#233240;margin-top:6px}
  .filet{margin:20px 48px 0 48px;border-top:1px solid #87b8e6}
  .inner{padding:22px 48px 30px 48px}
  .cols{display:flex;justify-content:space-between;gap:24px}
  .col-r{text-align:right}
  .lab{font-size:10px;letter-spacing:1.4px;color:#b4bcc4;font-weight:700;text-transform:uppercase}
  .val{font-size:12.5px;font-weight:700;color:#1f2933;margin-top:4px}
  .sub{font-size:11px;color:#9aa4b0;line-height:16px}
  .lines{margin-top:22px}
  .line{display:flex;justify-content:space-between;align-items:flex-start;padding:11px 0;border-bottom:.5px solid #eef1f4}
  .line:last-child{border-bottom:none}
  .line .des{font-size:13px;color:#1f2933}
  .line .det{font-size:11px;color:#b4bcc4;margin-top:2px}
  .line .amt{font-size:13.5px;font-weight:600;color:#1f2933}
  .total{margin-top:8px;border-top:1.5px solid #3d5166;padding-top:12px;display:flex;justify-content:space-between;align-items:baseline}
  .total .tl{font-size:13px;color:#5c6773}
  .total .ta{font-size:26px;font-weight:800;color:#3d5166}
  .note-ota{margin-top:16px;background:#f7fafc;border-radius:8px;padding:10px 14px;font-size:11px;color:#5c6773;line-height:1.5}
  .foot{background:#f7fafc;padding:12px 20px;text-align:center}
  .f1{font-size:10.5px;color:#9aa4b0}
  .f2{font-size:9.5px;color:#b4bcc4;margin-top:3px}
  .print-btn{position:fixed;top:20px;right:20px;background:#3d5166;color:#fff;border:none;border-radius:10px;padding:12px 20px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,.15);font-family:'Raleway',sans-serif;z-index:100}
  @media print{
    body{padding:0;background:#fff}
    .page{box-shadow:none;border-radius:0;max-width:100%}
    .print-btn{display:none!important}
    *{-webkit-print-color-adjust:exact;color-adjust:exact;print-color-adjust:exact}
  }
</style>
</head>
<body>
${mode === 'preview' ? `<button class="print-btn" onclick="window.print()">🖨️ Imprimer / Enregistrer en PDF</button>` : ''}
<div class="page">
  <div class="head">
    <div class="eyebrow">${isReceipt ? 'REÇU DE SÉJOUR' : 'FACTURE'}</div>
    <div class="logement">${escapeHtml(log.nom_complet || log.nom || log.id)}</div>
  </div>
  <div class="filet"></div>
  <div class="inner">
    <div class="cols">
      <div>
        <div class="lab">Pour</div>
        <div class="val">${escapeHtml(voyageurNom)}</div>
        <div class="sub">${[voyageurEmail ? escapeHtml(voyageurEmail) : '', voyageurTel ? escapeHtml(voyageurTel) : '', `${escapeHtml(nbVoyageurs)} voyageur${nbVoyageurs > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}</div>
      </div>
      <div class="col-r">
        <div class="lab">Séjour</div>
        <div class="val">Du ${escapeHtml(dateArrivee)} au ${escapeHtml(dateDepart)}</div>
        <div class="sub">${nuits} nuit${nuits > 1 ? 's' : ''}</div>
      </div>
    </div>
    <div class="lines">
      ${viaPlateforme ? `<div class="line">
        <div><div class="des">Séjour</div>
        <div class="det">${escapeHtml(nuitsSejour)} · facturé par ${escapeHtml(emitterDisplay)}</div></div>
        <div class="amt">${montantPlateforme ? `${fmtEuro(sejourFacture)} €` : '—'}</div>
      </div>
      <div class="line">
        <div><div class="des">Réservé sur</div></div>
        <div class="amt">${escapeHtml(nomPlateforme)}</div>
      </div>
      ${taxeFactureeParEmetteur ? `<div class="line">
        <div><div class="des">Taxe de séjour</div>
        <div class="det">facturée par ${escapeHtml(emitterDisplay)}</div></div>
        <div class="amt">${fmtEuro(montantPlateforme.taxeIncluse)} €</div>
      </div>` : ''}` : `<div class="line">
        <div><div class="des">Hébergement</div>
        <div class="det">${nuits} nuit${nuits > 1 ? 's' : ''} × ${fmtEuro(baseHebergement / nuits)} €</div></div>
        <div class="amt">${fmtEuro(baseHebergement)} €</div>
      </div>
      ${cleaningFee > 0 ? `<div class="line">
        <div><div class="des">Frais de ménage</div>
        ${isReceipt ? `<div class="det">forfait</div>` : ''}</div>
        <div class="amt">${fmtEuro(cleaningFee)} €</div>
      </div>` : ''}
      ${(isReceipt ? showReceiptTax : cityTax > 0) ? `<div class="line">
        <div><div class="des">Taxe de séjour</div></div>
        <div class="amt">${fmtEuro(cityTax)} €</div>
      </div>` : ''}`}
    </div>
    <div class="total">
      <span class="tl">${escapeHtml(totalLabel)}</span>
      <span class="ta">${viaPlateforme
        ? (montantPlateforme ? `${fmtEuro(montantFacture)}&nbsp;€` : '—')
        : `${fmtEuro(isReceipt ? receiptTotalPaye : totalAmount)}&nbsp;€`}</span>
    </div>
    ${viaPlateforme ? `
    <div class="note-ota">${escapeHtml(notePlateforme)}</div>` : ''}
  </div>
  <div class="foot">
    <div class="f1">${footerLine}</div>
    ${contactEmailDoc ? `<div class="f2">Une question ? ${escapeHtml(contactEmailDoc)}</div>` : ''}
  </div>
</div>
</body>
</html>`

  return { html, numFact, totalAmount: viaPlateforme ? montantFacture : totalAmount, voyageurEmail }
}

// Plateformes dont la règle est connue (décisions Dimitri, 08/10/2026).
// QUI PERÇOIT LA TAXE DE SÉJOUR dépend de l'ÉMETTEUR, pas seulement de la
// plateforme (relevés réels lus par Dimitri, 08/10/2026) :
//   · Airbnb : toujours perçue et reversée par Airbnb ;
//   · Booking, logement d'un propriétaire (son compte Booking) : Booking
//     retient la taxe et la reverse (rés. 5756114766 : 405,75 − taxe 22,55 −
//     commission 57,48 − frais 5,36 = 320,36 viré) ;
//   · Booking, Maison Zen (compte FULLBNBZEN) : AUCUNE ligne taxe, elle arrive
//     chez FULLBNBZEN qui la reverse lui-même (95,89 − 13,50 − 1,34 = 81,05).
const PLATEFORMES = {
  airbnb: { nom: 'Airbnb', taxePercueParPlateforme: () => true },
  booking: { nom: 'Booking.com', taxePercueParPlateforme: (typeEmetteur) => typeEmetteur === 'proprio' },
}

// ══ LE MONTANT FACTURÉ PAR L'ÉMETTEUR SUR UNE PLATEFORME — UNE SEULE FONCTION ══
// (décision 34 du 30/10, lot A1). Formules PROPOSÉES, à valider par Dimitri
// sur relevé (paquet du matin) :
//   · Airbnb  = total de la réservation − taxe perçue par Airbnb. En base, la
//     taxe des séjours Airbnb vaut 0 (Beds24 la donne à 0, l'import ne la
//     garde pas) : la formule rend donc le TOTAL, comme proposé ;
//   · Booking, logement d'un propriétaire = total − taxe de séjour (Booking
//     la retient et la reverse) ;
//   · Booking, Maison Zen (FULLBNBZEN) = total : la taxe arrive chez
//     FULLBNBZEN, qui la facture et la reverse (relevé Soleil du Sud du 08/10).
// Les frais de service de la plateforme ne figurent jamais : payés à la
// plateforme, pas à l'émetteur. Arrondi au centime.
// @returns {{montant:number, sejour:number, taxeIncluse:number}|null}
//   null si la plateforme n'a pas de règle (aucune pièce).
//   { erreur: 'montant_inconnu' } si le total n'est pas un nombre strictement
//   positif ; { erreur: 'taxe_inconnue' } si la taxe est illisible, ou
//   ABSENTE sur Booking (jamais « total taxe comprise » par défaut). Sur
//   Airbnb, une taxe absente vaut 0 : elle est toujours payée à Airbnb.
//   Jamais `Number(x) || 0` (relecture équipe rouge, 30/10).
function montantFacturePlateforme(reservation, typeEmetteur) {
  const r = reservation || {}
  const plateforme = PLATEFORMES[r.source]
  if (!plateforme) return null
  const centime = (n) => Math.round(n * 100) / 100
  const lire = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null
    if (typeof v === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(v)) return Number(v)
    return null
  }
  const total = lire(r.total_amount)
  if (total === null || !(total > 0)) return { erreur: 'montant_inconnu' }
  let taxe = lire(r.city_tax)
  if (taxe === null) {
    if (r.city_tax == null && r.source === 'airbnb') taxe = 0
    else return { erreur: 'taxe_inconnue' }
  }
  if (taxe < 0 || taxe > total) return { erreur: 'taxe_inconnue' }
  const sejour = centime(Math.max(0, total - taxe))
  const taxeIncluse = plateforme.taxePercueParPlateforme(typeEmetteur) ? 0 : centime(taxe)
  return { montant: centime(sejour + taxeIncluse), sejour, taxeIncluse }
}

function escapeHtml(s) {
  if (s == null) return ''
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

module.exports = { genFactureResaHTML, montantFacturePlateforme, PLATEFORMES }
